import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Chip, Divider, InputAdornment, Popover, TextField, Typography,
} from '@mui/material';
import { DateRange as DateRangeIcon } from '@mui/icons-material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  format, isSameDay, isWithinInterval, startOfDay,
} from 'date-fns';
import { el } from 'date-fns/locale';

/** Presets used on Financial Overview (Σήμερα, Τρέχων Μήνας, Τελευταίες 30 Ημέρες) */
export function getFinancialOverviewDatePresets(isMobile = false) {
  return [
    {
      id: 'today',
      label: 'Σήμερα',
      getRange: () => {
        const now = new Date();
        return { start: now, end: now };
      },
    },
    {
      id: 'current-month',
      label: isMobile ? 'Μήνας' : 'Τρέχων Μήνας',
      getRange: () => {
        const now = new Date();
        return {
          start: new Date(now.getFullYear(), now.getMonth(), 1),
          end: now,
        };
      },
    },
    {
      id: 'last-30-days',
      label: isMobile ? '30 ημ.' : 'Τελευταίες 30 Ημέρες',
      getRange: () => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 30);
        return { start, end };
      },
    },
  ];
}

function formatRangeLabel(startDate, endDate) {
  if (!startDate || !endDate) return '';
  const fmt = (d) => format(d, 'dd/MM/yyyy', { locale: el });
  return `${fmt(startDate)} – ${fmt(endDate)}`;
}

function normalizeDay(date) {
  return startOfDay(date);
}

function RangePickersDay(props) {
  const {
    day,
    rangeStart,
    rangeEnd,
    hoverDay,
    outsideCurrentMonth,
    ...other
  } = props;

  const dayNorm = normalizeDay(day);
  const startNorm = rangeStart ? normalizeDay(rangeStart) : null;
  const endNorm = rangeEnd ? normalizeDay(rangeEnd) : null;
  const hoverNorm = hoverDay ? normalizeDay(hoverDay) : null;

  const previewEnd = endNorm || (startNorm && hoverNorm ? hoverNorm : null);
  let inRange = false;

  if (startNorm && previewEnd) {
    const from = startNorm <= previewEnd ? startNorm : previewEnd;
    const to = startNorm <= previewEnd ? previewEnd : startNorm;
    inRange = isWithinInterval(dayNorm, { start: from, end: to })
      && !isSameDay(dayNorm, from)
      && !isSameDay(dayNorm, to);
  }

  const isStart = startNorm && isSameDay(dayNorm, startNorm);
  const isEnd = endNorm && isSameDay(dayNorm, endNorm);
  const isSelected = isStart || isEnd;

  return (
    <PickersDay
      {...other}
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      selected={isSelected}
      sx={{
        ...(inRange && {
          bgcolor: 'rgba(0, 128, 96, 0.18)',
          color: 'text.primary',
          borderRadius: 0,
          '&:hover, &:focus': { bgcolor: 'rgba(0, 128, 96, 0.28)' },
        }),
        ...(isStart && {
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          borderTopLeftRadius: '50%',
          borderBottomLeftRadius: '50%',
          '&:hover, &:focus': { bgcolor: 'primary.dark' },
        }),
        ...(isEnd && {
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          borderTopRightRadius: '50%',
          borderBottomRightRadius: '50%',
          '&:hover, &:focus': { bgcolor: 'primary.dark' },
        }),
        ...(isStart && isEnd && {
          borderRadius: '50%',
        }),
      }}
    />
  );
}

export default function DateRangePickerField({
  startDate,
  endDate,
  onRangeChange,
  isMobile = false,
  label = 'Περίοδος',
  size = 'small',
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [hoverDay, setHoverDay] = useState(null);

  const open = Boolean(anchorEl);
  const presets = useMemo(() => getFinancialOverviewDatePresets(isMobile), [isMobile]);

  useEffect(() => {
    if (open) {
      setRangeStart(startDate);
      setRangeEnd(endDate);
      setHoverDay(null);
    }
  }, [open, startDate, endDate]);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const applyRange = (start, end) => {
    onRangeChange(start, end);
    handleClose();
  };

  const handlePreset = (preset) => {
    const { start, end } = preset.getRange();
    applyRange(start, end);
  };

  const handleDaySelect = (day) => {
    if (!day) return;
    const picked = normalizeDay(day);

    if (!rangeStart || rangeEnd) {
      setRangeStart(picked);
      setRangeEnd(null);
      setHoverDay(null);
      return;
    }

    const start = rangeStart;
    if (isSameDay(picked, start)) {
      applyRange(start, start);
      return;
    }

    const [from, to] = picked < start ? [picked, start] : [start, picked];
    applyRange(from, to);
  };

  const selectionHint = !rangeStart || rangeEnd
    ? 'Κλικ: ημερομηνία έναρξης'
    : 'Κλικ: ημερομηνία λήξης';

  return (
    <>
      <TextField
        fullWidth
        size={size}
        label={label}
        value={formatRangeLabel(startDate, endDate)}
        onClick={handleOpen}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start">
              <DateRangeIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
          sx: { cursor: 'pointer', borderRadius: 2 },
        }}
        inputProps={{ 'aria-label': `${label}: ${formatRangeLabel(startDate, endDate)}` }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              p: 1.5,
              borderRadius: 2,
              width: { xs: 'min(100vw - 24px, 340px)', sm: 340 },
              boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
            },
          },
        }}
      >
        <Box sx={{ px: 0.5, pt: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Επιλογή περιόδου
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
            {presets.map((preset) => (
              <Chip
                key={preset.id}
                label={preset.label}
                size="small"
                clickable
                onClick={() => handlePreset(preset)}
                sx={{ fontWeight: 500 }}
              />
            ))}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, px: 0.5 }}>
            {selectionHint}
          </Typography>

          <Divider sx={{ mb: 0.5 }} />

          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={el}>
            <DateCalendar
              value={rangeEnd || rangeStart}
              referenceDate={rangeEnd || rangeStart || endDate || startDate || new Date()}
              onChange={handleDaySelect}
              slots={{ day: RangePickersDay }}
              slotProps={{
                day: (ownerState) => ({
                  rangeStart,
                  rangeEnd,
                  hoverDay,
                  onMouseEnter: () => {
                    if (rangeStart && !rangeEnd) {
                      setHoverDay(ownerState.day);
                    }
                  },
                  onMouseLeave: () => setHoverDay(null),
                }),
              }}
              sx={{
                width: '100%',
                maxHeight: 320,
                '& .MuiPickersCalendarHeader-root': { pl: 1, pr: 1 },
              }}
            />
          </LocalizationProvider>
        </Box>
      </Popover>
    </>
  );
}
